import {saltyrtcClient, saltyrtcTaskWebrtc} from "@ark-us/saltyrtc-transport";
import {draw, drawHistory, showAlert, cancelAlert, centre} from "./map";
import {disconnected, showMapPage} from "./index";

/**
 * Copyright (c) 2016-2019 Threema GmbH
 * Copyright (c) 2022 Simon Morgan
 *
 * Licensed under the Apache License, Version 2.0, <see LICENSE-APACHE file>
 * or the MIT license <see LICENSE-MIT file>, at your option. This file may not be
 * copied, modified, or distributed except according to those terms.
 */
const LOG_LEVEL = 'info';
const SALTY_HOST = 'sodium.slimjimsoftware.co.uk';
const SALTY_PORT = 443;
const DC_LABEL = 'much-secure';
const SALTY_SERVER_KEY = '537ff0441b7e227b6e20cca41b147b893b3ca66f47d36f74a52b6fee2b7adf44';

// ======================================

class FlowControlledDataChannel {
    private dc: any;
    private paused: boolean;
    protected ready: Promise<void>;
    private readonly highWaterMark: number;
    private resolve: (value: (PromiseLike<void> | void)) => void;

    constructor(dc, lowWaterMark = 262144, highWaterMark = 1048576) {
        this.dc = dc;
        this.paused = false;
        this.ready = Promise.resolve();
        this.highWaterMark = highWaterMark;

        // Drain once ready
        this.dc.bufferedAmountLowThreshold = lowWaterMark;
        this.dc.onbufferedamountlow = () => {
            // Continue once low water mark has been reached
            if (this.paused) {
                console.debug(`Data channel ${this.dc.label} resumed @ ${this.dc.bufferedAmount}`);
                this.paused = false;
                this.resolve();
            }
        };
    }

    write(message) {
        // Throw if paused
        if (this.paused) {
            throw new Error('Unable to write, data channel is paused!');
        }

        // Try sending
        // Note: Technically we should be able to catch a TypeError in case the
        //       underlying buffer is full. However, there are other reasons
        //       that can result in a TypeError and no browser has implemented
        //       this properly so far. Thus, we use a well-tested high water
        //       mark instead and try to never fill the buffer completely.
        this.dc.send(message);

        // Pause once high water mark has been reached
        if (!this.paused && this.dc.bufferedAmount >= this.highWaterMark) {
            this.paused = true;
            this.ready = new Promise((resolve) => this.resolve = resolve);
            console.debug(`Data channel ${this.dc.label} paused @ ${this.dc.bufferedAmount}`);
        }
    }
}

class UnboundedFlowControlledDataChannel extends FlowControlledDataChannel {
    private queue: Promise<void>;
    constructor(dc, lowWaterMark = 262144, highWaterMark = 1048576) {
        super(dc, lowWaterMark, highWaterMark);
        this.queue = this.ready;
    }

    write(message) {
        // Wait until ready, then write
        // Note: This very simple technique allows for ordered message
        //       queueing by using the event loop.
        this.queue = this.queue.then(async () => {
            await this.ready;
            super.write(message);
        });
    }
}

type SecureDataChannel = {
    rtcDataChannel: RTCDataChannel,
    unboundedFlowControlDataChannel: UnboundedFlowControlledDataChannel,
    crypto: saltyrtcClient.crypto,
    messageId: number,
}

export class RTCClient {
    private turnUser: String;
    private turnPass: String;
    private turnURLs: any;
    private client: saltyrtcClient;
    private task: any;
    private peerConnection: RTCPeerConnection;
    private debugDataChannel: RTCDataChannel;
    private secureDataChannel: SecureDataChannel;
    private timeout: NodeJS.Timeout;

    start(keyStore: any, iceServers: any, peerKey: any, timeout: NodeJS.Timeout) {
        this.turnURLs = iceServers.urls;
        this.turnUser = iceServers.username;
        this.turnPass = iceServers.credential;
        this.timeout = timeout;
        // Create SaltyRTC tasks
        const tasks = [
            new saltyrtcTaskWebrtc.WebRTCTaskBuilder().withLoggingLevel(LOG_LEVEL).withVersion('v1').build()
        ];

        // Create SaltyRTC client
        // noinspection JSCheckFunctionSignatures
        this.client = new saltyrtcClient.SaltyRTCBuilder()
            .withLoggingLevel(LOG_LEVEL)
            .connectTo(SALTY_HOST, SALTY_PORT)
            .withKeyStore(keyStore)
            .withServerKey(SALTY_SERVER_KEY)
            .withTrustedPeerKey(peerKey)
            .withPingInterval(30)
            .usingTasks(tasks)
            .asInitiator();
        this.client.on('state-change', this.onStateChange.bind(this));
        this.client.on('connection-error', this.onConnectionError.bind(this));
        this.client.on('connection-closed', this.onConnectionClosed.bind(this));
        this.client.connect();
    }

    onStateChange(newState: { data: string; }) {
        //console.debug('New state:', newState);
        //console.debug('#dataChannel-state = saltySignaling', newState.data);
        if (newState.data === 'task') {
            clearTimeout(this.timeout);
            // Store chosen task
            this.task = this.client.getTask();
            document.querySelector("#loadingState").innerHTML = "Connecting to device..."

            // Initialise WebRTC peer-to-peer connection
            this.initWebRTC();
        } else if (newState.data === 'closed') {
            document.querySelector("#loadingState").innerHTML = "Connection closed."
            disconnected();
        }
    }

    stop() {
        this.client.disconnect();
    }

    onConnectionError(ev) {
        //console.debug('Connection error:', ev);
    }

    onConnectionClosed(closeCode) {
        //console.debug('Connection was closed with code', closeCode);
    }

    initWebRTC() {
        //console.debug('Initialize WebRTC connection...');
        let iceServers = []
        // Create RTC peer connection
        this.turnURLs.forEach((url: { startsWith: (arg0: string) => any; }) => {
            if (url.startsWith("turn")) {
                iceServers.push({
                    urls: url,
                    username: this.turnUser,
                    credential: this.turnPass
                });
            } else {
                iceServers.push({ urls: url });
            }
        });
        this.peerConnection = new RTCPeerConnection({iceServers: iceServers});

        // Let the "negotiationneeded" event trigger offer generation
        this.peerConnection.onnegotiationneeded = () => {
            //console.debug('Negotiation needed...');
            this.initiatorFlow().catch((e) => {
                //console.error('Unable to send error:', e);
            });
        };

        // Handle state changes
        this.peerConnection.onsignalingstatechange = () => {
            //console.debug('RTC signaling state change:', this.peerConnection.signalingState);
        };
        this.peerConnection.onconnectionstatechange = () => {
            //console.debug('RTC connection state change:', this.peerConnection.connectionState);
            if (this.peerConnection.signalingState.toString() === "disconnected") {
                // peer has disconnected...
                disconnected();
            }
        };
        this.peerConnection.oniceconnectionstatechange = () => {
            //console.debug('ICE connection state change:', this.peerConnection.iceConnectionState);
        };
        this.peerConnection.onicegatheringstatechange = () => {
            //console.debug('ICE gathering state change:', this.peerConnection.iceGatheringState);
        };

        // Set up ICE candidate handling
        this.setupIceCandidateHandling();

        // Log incoming data channels
        this.peerConnection.ondatachannel = (e) => {
            //console.debug('New data channel was created:', e.channel.label);
        };

        // Create data channel for handover and initiate handover once open
        this.prepareHandover();

        // On handover, create the data channel
        this.client.on('handover', () => {
            console.info('Handover done');
            this.createDataChannel();
        });
    }

    setupIceCandidateHandling() {
        console.debug('Setting up ICE candidate handling...');
        this.peerConnection.onicecandidate = (e) => {
            // if (e.candidate) {
            //     console.debug('New local candidate:', e.candidate.candidate);
            // } else {
            //     console.debug('New local candidate:', e.candidate);
            // }
            if (e.candidate) {
                this.task.sendCandidate({
                    candidate: e.candidate.candidate,
                    sdpMid: e.candidate.sdpMid,
                    sdpMLineIndex: e.candidate.sdpMLineIndex,
                });
            }
            //console.debug('#dataChannel-state = iceGathering', this.peerConnection.iceGatheringState);
        };
        this.peerConnection.onicecandidateerror = (e) => console.error('ICE candidate error:', e);

        this.task.on('candidates', (e) => {
            for (const candidateInit of e.data) {
                //console.debug('New remote candidate:', candidateInit.candidate);
                this.peerConnection.addIceCandidate(candidateInit).catch((e) => {
                    //console.error('Unable to add ICE candidate:', e);
                });
            }
        });
    }

    prepareHandover() {
        // Get transport link
        const link = this.task.getTransportLink();

        // Create data channel
        const dataChannel = this.peerConnection.createDataChannel(link.label, {
            id: link.id,
            negotiated: true,
            ordered: true,
            protocol: link.protocol,
        });
        dataChannel.binaryType = 'arraybuffer';

        // Wrap as unbounded, flow-controlled data channel
        const unboundedChannel = new UnboundedFlowControlledDataChannel(dataChannel);

        // Bind events
        dataChannel.onopen = () => {
            //console.info(`Data channel ${dataChannel.label} open`);

            // Rebind close event
            dataChannel.onclose = () => {
                //console.info(`Data channel ${dataChannel.label} closed`);
                link.closed();
            };

            // Initiate handover
            this.task.handover(() => {
                // transport handler
                function getMaxMessageSize() {
                    return this.pc.sctp.maxMessageSize;
                }
                function close() {
                    //console.debug(`Data channel ${dataChannel.label} close request`);
                    dataChannel.close();
                }
                function send(message) {
                    //console.debug(`Data channel ${dataChannel.label} outgoing signaling message of length ${message.byteLength}`);
                    unboundedChannel.write(message);
                }
            });
        };
        dataChannel.onclose = () => {
            //console.error(`Data channel ${dataChannel.label} closed`);
        };
        dataChannel.onerror = (event) => {
            //console.error(`Data channel ${dataChannel.label} error:`, event);
        };
        dataChannel.onmessage = (event) => {
            //console.debug(`Data channel ${dataChannel.label} incoming signaling message of length ${event.data.byteLength}`);
            link.receive(new Uint8Array(event.data));
        };

        // Attach to this for debug purposes
        // noinspection JSUnusedGlobalSymbols
        this.debugDataChannel = dataChannel;
    }

    async initiatorFlow() {
        // Register answer handler
        this.task.once('answer', async (answer) => {
            //console.warn('Answer', answer);
            //console.debug('Set remote description');
            await this.peerConnection.setRemoteDescription(answer.data);
            //console.info('WebRTC initialization done.');
        });

        // Create offer
        //console.debug('Create offer');
        const offer = await this.peerConnection.createOffer();
        //console.warn('Offer', offer);
        //console.debug('Set local description');
        await this.peerConnection.setLocalDescription(offer);
        //console.debug('Send offer to peer');
        // noinspection JSCheckFunctionSignatures
        this.task.sendOffer(offer);
    }

    createDataChannel() {
        // Create channel
        const rtcDataChannel = this.peerConnection.createDataChannel(DC_LABEL);
        rtcDataChannel.binaryType = 'arraybuffer';

        // Wrap as unbounded, flow-controlled data channel
        const unboundedDataChannel = new UnboundedFlowControlledDataChannel(rtcDataChannel);

        // Create crypto context
        // Note: We need to apply encrypt-then-chunk for backwards compatibility reasons.
        const crypto = this.task.createCryptoContext(rtcDataChannel.id);

        // Create unchunker
        // Note: We need to use an unreliable unordered unchunker for backwards compatibility reasons.
        // @ts-ignore
        const unchunker = new chunkedDc.UnreliableUnorderedUnchunker();

        // Bind events
        rtcDataChannel.onopen = () => {
            //console.info(`Data channel ${rtcDataChannel.label} open`);
            showMapPage();
        };
        rtcDataChannel.onclose = () => {
            //console.info(`Data channel ${rtcDataChannel.label} closed`);
            alert("Data channel closed!")
            // TODO: reconnect?
            // this.initWebRTC();
        };
        rtcDataChannel.onerror = (event) => {
            //console.error(`Data channel ${rtcDataChannel.label} error:`, event);
            alert("Error" + event)
        };
        rtcDataChannel.onmessage = (event) => {
            //console.debug(`Data channel ${rtcDataChannel.label} incoming chunk of length ${event.data.byteLength}`);
            unchunker.add(new Uint8Array(event.data));
        };
        // noinspection JSUndefinedPropertyAssignment
        unchunker.onMessage = (array) => {
            const box = saltyrtcClient.Box.fromUint8Array(array, saltyrtcTaskWebrtc.DataChannelCryptoContext.NONCE_LENGTH);
            const message = crypto.decrypt(box);
            //console.debug(`Data channel ${rtcDataChannel.label} incoming message of length ${message.byteLength}`);

            // Convert to string
            // TODO: This is ugly... we should use a separate channel instead
            let text = new TextDecoder().decode(message);
            // if (message.byteLength < 255) {
            //     text = `[${Math.trunc(message.byteLength / 1024)} KiB binary data]`;
            // }

            let dataJson = JSON.parse(text);
            if (dataJson.ALARM_STATUS) {
                draw(dataJson);
                if (dataJson.TRACKING_ONLY) {
                    centre();
                } else {
                    if (dataJson.IN_SAFE_ZONE) {
                        cancelAlert();
                    } else {
                        showAlert();
                    }
                }
            } else if (dataJson.LOCATION_HISTORY_LIST) {
                drawHistory(dataJson);
            } else {
                this.sendDataChannel(new TextEncoder().encode("REQUEST_DATA"));
                // TODO: inform user we are waiting for data
                console.debug("Waiting on data from peer...");
            }
        };

        // Attach to this
        this.secureDataChannel = {
            rtcDataChannel: rtcDataChannel,
            unboundedFlowControlDataChannel: unboundedDataChannel,
            crypto: crypto,
            messageId: 0,
        };
    }

    sendDataChannel(message) {
        //console.debug(`Data channel ${this.secureDataChannel.rtcDataChannel.label} outgoing message of length ${message.byteLength}`);
        const box = this.secureDataChannel.crypto.encrypt(message);
        const chunkLength = Math.min(262144, this.peerConnection.sctp.maxMessageSize);
        // @ts-ignore
        const chunker = new chunkedDc.UnreliableUnorderedChunker(this.secureDataChannel.messageId++, box.toUint8Array(), chunkLength);
        for (const chunk of chunker) {
            //console.debug(`Data channel ${this.secureDataChannel.rtcDataChannel.label} outgoing chunk of length ${chunk.byteLength}`);
            this.secureDataChannel.unboundedFlowControlDataChannel.write(chunk);
        }
    }

}